import React, { useState, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'

// Utils
import { cn } from '@utils/cn'

// Types
import type { FC, FormEvent, KeyboardEvent } from 'react'

interface SearchBarProps {
  placeholder?: string
  initialValue?: string
  size?: 'sm' | 'md' | 'lg'
  variant?: 'default' | 'minimal' | 'floating'
  showSuggestions?: boolean
  suggestions?: string[]
  onSearch?: (query: string) => void
  onClear?: () => void
  className?: string
  autoFocus?: boolean
  disabled?: boolean
}

const SearchBar: FC<SearchBarProps> = ({
  placeholder = 'ค้นหาบริษัทรักษาความปลอดภัย...',
  initialValue = '',
  size = 'md',
  variant = 'default',
  showSuggestions = true,
  suggestions = [],
  onSearch,
  onClear,
  className,
  autoFocus = false,
  disabled = false
}) => {
  const [query, setQuery] = useState(initialValue)
  const [isFocused, setIsFocused] = useState(false)
  const [showSuggestionsList, setShowSuggestionsList] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Popular search terms (can be fetched from API)
  const defaultSuggestions = [
    'บริษัทรักษาความปลอดภัย กรุงเทพ',
    'รปภ. โรงงาน',
    'ยามรักษาการณ์ คอนโด',
    'รักษาความปลอดภัย อาคาร',
    'บริการรักษาความปลอดภัย',
    'security company bangkok',
    'ยาม รถเงิน',
    'รปภ. ห้างสรรพสินค้า'
  ]

  const filteredSuggestions = query
    ? [...suggestions, ...defaultSuggestions].filter(suggestion =>
        suggestion.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 6)
    : defaultSuggestions.slice(0, 6)

  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault()
    if (!query.trim()) return

    const searchQuery = query.trim()

    if (onSearch) {
      onSearch(searchQuery)
    } else {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`)
    }

    setShowSuggestionsList(false)
  }, [query, onSearch, navigate])

  const handleSuggestionClick = useCallback((suggestion: string) => {
    setQuery(suggestion)
    setShowSuggestionsList(false)

    if (onSearch) {
      onSearch(suggestion)
    } else {
      navigate(`/search?q=${encodeURIComponent(suggestion)}`)
    }
  }, [onSearch, navigate])

  const handleClear = useCallback(() => {
    setQuery('')
    setShowSuggestionsList(false)

    // If on search page, reset all search parameters
    if (location.pathname === '/search') {
      navigate('/search')
    }

    onClear?.()
  }, [onClear, location.pathname, navigate])

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setShowSuggestionsList(false)
      e.currentTarget.blur()
    }
  }, [])

  const sizeClasses = {
    sm: 'h-9 text-sm',
    md: 'h-11 text-base',
    lg: 'h-14 text-lg'
  }

  const variantClasses = {
    default: 'bg-white border border-neutral-300 shadow-sm',
    minimal: 'bg-neutral-50 border border-transparent',
    floating: 'bg-white border border-neutral-200 shadow-feature'
  }

  return (
    <div className={cn('relative w-full', className)}>
      <form onSubmit={handleSubmit} className="relative">
        <div className="relative">
          {/* Search Icon */}
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-neutral-400" />
          </div>

          {/* Search Input */}
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              setIsFocused(true)
              if (showSuggestions) setShowSuggestionsList(true)
            }}
            onBlur={() => {
              setIsFocused(false)
              // Delay hiding suggestions to allow clicks
              setTimeout(() => setShowSuggestionsList(false), 150)
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            disabled={disabled}
            className={cn(
              'block w-full pl-10 pr-10 font-thai placeholder:text-neutral-400 transition-all duration-200',
              'focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:outline-none',
              'disabled:bg-neutral-100 disabled:text-neutral-500 disabled:cursor-not-allowed',
              sizeClasses[size],
              variantClasses[variant],
              'rounded-xl'
            )}
          />

          {/* Action Button - Clear or Search */}
          <div className="absolute inset-y-0 right-0 flex items-center">
            {query ? (
              <button
                type="button"
                onClick={handleClear}
                className="pr-3 text-neutral-400 hover:text-neutral-600 transition-colors duration-200"
                title="ล้างข้อความ"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : (
              <button
                type="submit"
                className="pr-3 text-primary-500 hover:text-primary-700 transition-colors duration-200"
                title="ค้นหา"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Hidden Submit Button for Accessibility */}
        <button type="submit" className="sr-only" tabIndex={-1}>
          ค้นหา
        </button>
      </form>

      {/* Search Suggestions */}
      <AnimatePresence>
        {showSuggestions && showSuggestionsList && filteredSuggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute z-50 w-full mt-2 bg-white border border-neutral-200 rounded-xl shadow-feature overflow-hidden"
          >
            <div className="py-2">
              {/* Popular Searches Header (when no query) */}
              {!query && (
                <div className="px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide border-b border-neutral-100">
                  การค้นหายอดนิยม
                </div>
              )}

              {/* Suggestions List */}
              {filteredSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50 hover:text-primary-600 transition-colors duration-150 flex items-center space-x-3"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 text-neutral-400 flex-shrink-0" />
                  <span className="truncate font-thai">{suggestion}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SearchBar